import { useState, useMemo } from 'react';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, MONTHS, ExpenseCategory } from '../types';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { 
  Download,
  FileText,
  Loader2,
  CheckCircle,
  Package,
  Calendar,
  AlertCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function Export() {
  const { expenses } = useExpenses();
  
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedCategories, setSelectedCategories] = useState<ExpenseCategory[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState<{ name: string; url: string }[]>([]);
  const [error, setError] = useState('');
  const [expandedBinders, setExpandedBinders] = useState<ExpenseCategory[]>([]);
  const [generationProgress, setGenerationProgress] = useState<{ current: number; total: number; filename: string } | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number; filename: string } | null>(null);

  // Get years from 2020 to current year (matching Google Drive structure)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const allYears: number[] = [];
    for (let year = currentYear; year >= 2020; year--) {
      allYears.push(year);
    }
    return allYears;
  }, []);

  // Get expenses for selected month/year
  const monthExpenses = useMemo(() => {
    return expenses.filter(e => e.month === selectedMonth && e.year === selectedYear);
  }, [expenses, selectedMonth, selectedYear]);

  // Group by category
  const expensesByCategory = useMemo(() => {
    const grouped: Record<ExpenseCategory, typeof expenses> = {} as Record<ExpenseCategory, typeof expenses>;
    CATEGORIES.forEach(cat => {
      grouped[cat.value] = monthExpenses.filter(e => e.category === cat.value);
    });
    return grouped;
  }, [monthExpenses]);

  const toggleCategory = (cat: ExpenseCategory) => {
    setSelectedCategories(prev => 
      prev.includes(cat) 
        ? prev.filter(c => c !== cat)
        : [...prev, cat]
    );
  };

  const selectAll = () => {
    const catsWithDocs = CATEGORIES.filter(cat => expensesByCategory[cat.value].length > 0).map(c => c.value);
    setSelectedCategories(catsWithDocs);
  };

  const clearSelection = () => {
    setSelectedCategories([]);
  };

  const toggleBinderExpand = (cat: ExpenseCategory) => {
    setExpandedBinders(prev => 
      prev.includes(cat) 
        ? prev.filter(c => c !== cat)
        : [...prev, cat]
    );
  };

  // Convert base64 data URL to blob
  const dataURLtoBlob = (dataURL: string): Blob => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Fetch file bytes - handles both URLs and base64 data
  const fetchFileBytes = async (fileData: string, fileType: string): Promise<{ bytes: ArrayBuffer; detectedType: string }> => {
    // Check if it's a base64 data URL
    if (fileData.startsWith('data:')) {
      const blob = dataURLtoBlob(fileData);
      // Extract MIME type from data URL
      const mimeMatch = fileData.match(/^data:([^;,]+)/);
      const detectedType = mimeMatch ? mimeMatch[1] : fileType;
      return { bytes: await blob.arrayBuffer(), detectedType };
    }
    
    // It's a URL - fetch it
    console.log(`Fetching file from URL: ${fileData}`);
    const response = await fetch(fileData);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
    }
    
    // Get content type from response headers
    const contentType = response.headers.get('content-type') || fileType;
    console.log(`File content-type: ${contentType}`);
    
    return { bytes: await response.arrayBuffer(), detectedType: contentType };
  };
  
  // Detect actual file type from bytes (magic numbers)
  const detectFileType = (bytes: ArrayBuffer): string | null => {
    const arr = new Uint8Array(bytes);
    
    // PDF: starts with %PDF
    if (arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46) {
      return 'application/pdf';
    }
    
    // JPEG: starts with FF D8 FF
    if (arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF) {
      return 'image/jpeg';
    }
    
    // PNG: starts with 89 50 4E 47
    if (arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47) {
      return 'image/png';
    }
    
    // GIF: starts with GIF
    if (arr[0] === 0x47 && arr[1] === 0x49 && arr[2] === 0x46) {
      return 'image/gif';
    }
    
    // WebP: starts with RIFF....WEBP
    if (arr[0] === 0x52 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x46 &&
        arr[8] === 0x57 && arr[9] === 0x45 && arr[10] === 0x42 && arr[11] === 0x50) {
      return 'image/webp';
    }
    
    return null;
  };
  
  // Get file type from filename extension
  const getTypeFromFilename = (filename: string): string | null => {
    const ext = filename.toLowerCase().split('.').pop();
    const extMap: Record<string, string> = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'txt': 'text/plain',
    };
    return extMap[ext || ''] || null;
  };
  
  // Convert text content to PDF page(s)
  const addTextAsPdfPage = async (
    pdfDoc: PDFDocument, 
    textContent: string, 
    title: string
  ): Promise<boolean> => {
    try {
      const font = await pdfDoc.embedFont(StandardFonts.Courier);
      const fontSize = 10;
      const margin = 50;
      const pageWidth = 612;
      const pageHeight = 792;
      const lineHeight = fontSize * 1.2;
      const maxWidth = pageWidth - (margin * 2);
      const maxLinesPerPage = Math.floor((pageHeight - margin * 2 - 30) / lineHeight);
      
      // Split text into lines
      const lines: string[] = [];
      const rawLines = textContent.split('\n');
      
      for (const rawLine of rawLines) {
        // Wrap long lines
        if (rawLine.length * (fontSize * 0.6) > maxWidth) {
          const words = rawLine.split(' ');
          let currentLine = '';
          for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            if (testLine.length * (fontSize * 0.6) > maxWidth) {
              if (currentLine) lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          }
          if (currentLine) lines.push(currentLine);
        } else {
          lines.push(rawLine);
        }
      }
      
      // Create pages
      let lineIndex = 0;
      while (lineIndex < lines.length) {
        const page = pdfDoc.addPage([pageWidth, pageHeight]);
        
        // Add title header on first page
        if (lineIndex === 0) {
          page.drawText(title, {
            x: margin,
            y: pageHeight - margin,
            size: 12,
            font,
            color: rgb(0.2, 0.2, 0.2),
          });
          page.drawLine({
            start: { x: margin, y: pageHeight - margin - 15 },
            end: { x: pageWidth - margin, y: pageHeight - margin - 15 },
            thickness: 1,
            color: rgb(0.7, 0.7, 0.7),
          });
        }
        
        const startY = lineIndex === 0 ? pageHeight - margin - 35 : pageHeight - margin;
        const linesForThisPage = lineIndex === 0 ? maxLinesPerPage - 3 : maxLinesPerPage;
        
        for (let i = 0; i < linesForThisPage && lineIndex < lines.length; i++) {
          const line = lines[lineIndex];
          page.drawText(line || ' ', {
            x: margin,
            y: startY - (i * lineHeight),
            size: fontSize,
            font,
            color: rgb(0, 0, 0),
          });
          lineIndex++;
        }
      }
      
      return true;
    } catch (err) {
      console.error('Failed to convert text to PDF:', err);
      return false;
    }
  };

  const addMissingFilePlaceholderPage = async (
    pdfDoc: PDFDocument,
    expense: (typeof expenses)[number],
    reason: string
  ): Promise<boolean> => {
    try {
      const font = await pdfDoc.embedFont(StandardFonts.Courier);
      const page = pdfDoc.addPage([612, 792]);
      const margin = 50;
      const lineHeight = 14;
      let y = 792 - margin;

      const lines = [
        'Missing File Placeholder',
        '------------------------',
        `Filename: ${expense.filename || 'Unknown'}`,
        `Date: ${expense.date ? new Date(expense.date).toLocaleDateString() : 'Unknown'}`,
        `Amount: ${typeof expense.amount === 'number' ? `$${expense.amount.toFixed(2)}` : 'N/A'}`,
        `Description: ${expense.description || 'N/A'}`,
        `Reason: ${reason}`,
      ];

      for (const line of lines) {
        page.drawText(line, {
          x: margin,
          y,
          size: 12,
          font,
          color: rgb(0.2, 0.2, 0.2),
        });
        y -= lineHeight;
      }

      return true;
    } catch (err) {
      console.error('Failed to add missing file placeholder page:', err);
      return false;
    }
  };

  const generatePDFBinder = async (category: ExpenseCategory): Promise<{ name: string; blob: Blob } | null> => {
    const categoryExpenses = expensesByCategory[category];
    if (categoryExpenses.length === 0) return null;

    const pdfDoc = await PDFDocument.create();
    const categoryLabel = CATEGORIES.find(c => c.value === category)?.label || category;
    
    let successCount = 0;
    let failCount = 0;
    let placeholderCount = 0;

    for (let i = 0; i < categoryExpenses.length; i++) {
      const expense = categoryExpenses[i];
      setGenerationProgress({ 
        current: i + 1, 
        total: categoryExpenses.length, 
        filename: expense.filename 
      });
      
      try {
        console.log(`Processing ${i + 1}/${categoryExpenses.length}: ${expense.filename} (stored type: ${expense.fileType})`);
        
        // Fetch the file bytes (works for both URL and base64)
        const { bytes: fileBytes, detectedType } = await fetchFileBytes(expense.fileData, expense.fileType);
        
        // Try to detect actual file type - priority order:
        // 1. Magic bytes detection (most reliable)
        // 2. Stored fileType from database
        // 3. Filename extension (fallback)
        // 4. Content-type header from fetch (least reliable - often wrong)
        const magicType = detectFileType(fileBytes);
        const filenameType = getTypeFromFilename(expense.filename);
        
        // Use magic bytes first, then stored type, then filename extension
        let actualType = magicType;
        if (!actualType || actualType === 'text/plain') {
          actualType = expense.fileType;
        }
        if (!actualType || actualType === 'text/plain' || actualType === 'application/octet-stream') {
          actualType = filenameType || detectedType;
        }
        
        console.log(`Type detection for ${expense.filename}: magic=${magicType}, stored=${expense.fileType}, filename=${filenameType}, using=${actualType}`);
        
        // Determine if we should try PDF, image, or text based on type OR filename
        const lowerFilename = expense.filename.toLowerCase();
        const hasPdfExt = lowerFilename.endsWith('.pdf');
        const shouldTryPdf = actualType === 'application/pdf' || 
                            actualType?.includes('pdf') || 
                            hasPdfExt;
        const shouldTryImage = actualType?.startsWith('image/') || 
                              actualType?.includes('jpeg') || 
                              actualType?.includes('jpg') || 
                              actualType?.includes('png') ||
                              lowerFilename.match(/\.(jpg|jpeg|png|gif|webp)$/);
        const shouldTryText = (actualType?.startsWith('text/') && !hasPdfExt) || lowerFilename.endsWith('.txt');
        
        if (shouldTryText) {
          try {
            const decoder = new TextDecoder('utf-8');
            const textContent = decoder.decode(fileBytes);
            const printableRatio = textContent.split('').filter(c => 
              c.charCodeAt(0) >= 32 && c.charCodeAt(0) <= 126 || c === '\n' || c === '\r' || c === '\t'
            ).length / textContent.length;

            if (printableRatio > 0.8 && textContent.trim().length > 0) {
              const added = await addTextAsPdfPage(pdfDoc, textContent, expense.filename);
              if (added) {
                successCount++;
                console.log(`✓ Added text: ${expense.filename}`);
              } else {
                const placeholderAdded = await addMissingFilePlaceholderPage(pdfDoc, expense, 'Failed to render text content');
                if (placeholderAdded) {
                  placeholderCount++;
                  successCount++;
                } else {
                  failCount++;
                }
              }
            } else {
              console.error(`File ${expense.filename} doesn't appear to be valid text (${Math.round(printableRatio * 100)}% printable)`);
              const placeholderAdded = await addMissingFilePlaceholderPage(pdfDoc, expense, 'Text content not readable');
              if (placeholderAdded) {
                placeholderCount++;
                successCount++;
              } else {
                failCount++;
              }
            }
          } catch (textErr) {
            console.error(`Text conversion failed for ${expense.filename}:`, textErr);
            const placeholderAdded = await addMissingFilePlaceholderPage(pdfDoc, expense, 'Text conversion failed');
            if (placeholderAdded) {
              placeholderCount++;
              successCount++;
            } else {
              failCount++;
            }
          }
        } else if (shouldTryPdf) {
          // Embed existing PDF
          try {
            const existingPdf = await PDFDocument.load(fileBytes, { ignoreEncryption: true });
            const pages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
            pages.forEach(page => pdfDoc.addPage(page));
            successCount++;
            console.log(`✓ Added PDF: ${expense.filename}`);
          } catch (pdfErr: any) {
            console.warn(`PDF load failed for ${expense.filename}: ${pdfErr.message}`);
            
            // PDF loading failed - try to convert as text content
            // This handles files that were saved as text with .pdf extension
            if (pdfErr.message?.includes('No PDF header') || pdfErr.message?.includes('Failed to parse')) {
              console.log(`Attempting text-to-PDF conversion for ${expense.filename}...`);
              try {
                // Decode the bytes as text
                const decoder = new TextDecoder('utf-8');
                const textContent = decoder.decode(fileBytes);
                
                // Check if it looks like text (not binary garbage)
                const printableRatio = textContent.split('').filter(c => 
                  c.charCodeAt(0) >= 32 && c.charCodeAt(0) <= 126 || c === '\n' || c === '\r' || c === '\t'
                ).length / textContent.length;
                
                if (printableRatio > 0.8 && textContent.trim().length > 0) {
                  const added = await addTextAsPdfPage(pdfDoc, textContent, expense.filename);
                  if (added) {
                    successCount++;
                    console.log(`✓ Added as TEXT->PDF: ${expense.filename}`);
                  } else {
                    failCount++;
                  }
                } else {
                  console.error(`File ${expense.filename} doesn't appear to be valid text (${Math.round(printableRatio * 100)}% printable)`);
                  failCount++;
                }
              } catch (textErr) {
                console.error(`Text conversion failed for ${expense.filename}:`, textErr);
                failCount++;
              }
            } else if (expense.filename.toLowerCase().match(/\.(jpg|jpeg|png)$/)) {
              // Try as image
              try {
                const image = await pdfDoc.embedJpg(fileBytes);
                const page = pdfDoc.addPage([612, 792]);
                const scale = Math.min((612 - 40) / image.width, (792 - 40) / image.height);
                page.drawImage(image, {
                  x: (612 - image.width * scale) / 2,
                  y: (792 - image.height * scale) / 2,
                  width: image.width * scale,
                  height: image.height * scale,
                });
                successCount++;
                console.log(`✓ Added as image (PDF failed): ${expense.filename}`);
              } catch {
                const placeholderAdded = await addMissingFilePlaceholderPage(pdfDoc, expense, 'PDF failed and image fallback failed');
                if (placeholderAdded) {
                  placeholderCount++;
                  successCount++;
                } else {
                  failCount++;
                }
              }
            } else {
              const placeholderAdded = await addMissingFilePlaceholderPage(pdfDoc, expense, 'PDF processing failed');
              if (placeholderAdded) {
                placeholderCount++;
                successCount++;
              } else {
                failCount++;
              }
            }
          }
        } else if (shouldTryImage) {
          // Embed image as PDF page
          try {
            let image;
            if (actualType.includes('png')) {
              image = await pdfDoc.embedPng(fileBytes);
            } else {
              // For JPEG and other formats, try JPEG embedding
              image = await pdfDoc.embedJpg(fileBytes);
            }

            // Create a page with the image (Letter size)
            const page = pdfDoc.addPage([612, 792]);
            const scale = Math.min(
              (612 - 40) / image.width,
              (792 - 40) / image.height
            );
            
            const scaledWidth = image.width * scale;
            const scaledHeight = image.height * scale;
            
            page.drawImage(image, {
              x: (612 - scaledWidth) / 2,
              y: (792 - scaledHeight) / 2,
              width: scaledWidth,
              height: scaledHeight,
            });
            successCount++;
            console.log(`✓ Added image: ${expense.filename}`);
          } catch (imgErr) {
            console.error(`Failed to embed image ${expense.filename}:`, imgErr);
            // Try as JPEG if PNG failed
            if (actualType.includes('png')) {
              try {
                const image = await pdfDoc.embedJpg(fileBytes);
                const page = pdfDoc.addPage([612, 792]);
                const scale = Math.min((612 - 40) / image.width, (792 - 40) / image.height);
                page.drawImage(image, {
                  x: (612 - image.width * scale) / 2,
                  y: (792 - image.height * scale) / 2,
                  width: image.width * scale,
                  height: image.height * scale,
                });
                successCount++;
                console.log(`✓ Added image (as JPG fallback): ${expense.filename}`);
              } catch (fallbackErr) {
                console.error(`Fallback also failed for ${expense.filename}:`, fallbackErr);
                failCount++;
              }
            } else {
              const placeholderAdded = await addMissingFilePlaceholderPage(pdfDoc, expense, 'Image processing failed');
              if (placeholderAdded) {
                placeholderCount++;
                successCount++;
              } else {
                failCount++;
              }
            }
          }
        } else {
          // Last resort: try to load based on filename extension anyway
          console.warn(`⚠ Unknown type: ${actualType} for ${expense.filename}, trying based on extension...`);
          
          const filename = expense.filename.toLowerCase();
          let handled = false;
          
          if (filename.endsWith('.pdf')) {
            try {
              const existingPdf = await PDFDocument.load(fileBytes, { ignoreEncryption: true });
              const pages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
              pages.forEach(page => pdfDoc.addPage(page));
              successCount++;
              console.log(`✓ Added PDF (by extension): ${expense.filename}`);
              handled = true;
            } catch (e) {
              console.error(`Failed PDF by extension for ${expense.filename}:`, e);
            }
          }
          
          if (!handled && (filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.png'))) {
            try {
              const image = filename.endsWith('.png') 
                ? await pdfDoc.embedPng(fileBytes)
                : await pdfDoc.embedJpg(fileBytes);
              const page = pdfDoc.addPage([612, 792]);
              const scale = Math.min((612 - 40) / image.width, (792 - 40) / image.height);
              page.drawImage(image, {
                x: (612 - image.width * scale) / 2,
                y: (792 - image.height * scale) / 2,
                width: image.width * scale,
                height: image.height * scale,
              });
              successCount++;
              console.log(`✓ Added image (by extension): ${expense.filename}`);
              handled = true;
            } catch (e) {
              console.error(`Failed image by extension for ${expense.filename}:`, e);
            }
          }
          
          if (!handled) {
            console.error(`✗ Could not process ${expense.filename} - unsupported type: ${actualType}`);
            const placeholderAdded = await addMissingFilePlaceholderPage(pdfDoc, expense, `Unsupported type: ${actualType || 'unknown'}`);
            if (placeholderAdded) {
              placeholderCount++;
              successCount++;
            } else {
              failCount++;
            }
          }
        }
      } catch (err) {
        console.error(`✗ Failed to process ${expense.filename}:`, err);
        const placeholderAdded = await addMissingFilePlaceholderPage(pdfDoc, expense, 'File missing or could not be fetched');
        if (placeholderAdded) {
          placeholderCount++;
          successCount++;
        } else {
          failCount++;
        }
      }
    }

    setGenerationProgress(null);
    
    if (pdfDoc.getPageCount() === 0) {
      console.error(`No pages generated for ${category}. Success: ${successCount}, Failed: ${failCount}`);
      return null;
    }

    console.log(`Generated ${category} binder: ${successCount} pages, ${placeholderCount} placeholders, ${failCount} failed`);

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
    const monthName = MONTHS[selectedMonth - 1];
    const filename = `${categoryLabel.replace(/\s+/g, '_')}_${monthName}_${selectedYear}.pdf`;

    return { name: filename, blob };
  };

  const handleGenerate = async () => {
    if (selectedCategories.length === 0) {
      setError('Please select at least one category');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedFiles([]);

    try {
      const files: { name: string; url: string }[] = [];

      for (const category of selectedCategories) {
        const result = await generatePDFBinder(category);
        if (result) {
          const url = URL.createObjectURL(result.blob);
          files.push({ name: result.name, url });
        }
      }

      setGeneratedFiles(files);
    } catch (err) {
      setError('Failed to generate PDF binders. Please try again.');
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadAll = async () => {
    if (generatedFiles.length === 0) return;

    try {
      const zip = new JSZip();
      setDownloadProgress({ current: 0, total: generatedFiles.length, filename: '' });
      for (let i = 0; i < generatedFiles.length; i++) {
        const file = generatedFiles[i];
        setDownloadProgress({ current: i + 1, total: generatedFiles.length, filename: file.name });
        const response = await fetch(file.url);
        if (!response.ok) {
          throw new Error(`Failed to read ${file.name} for zip download`);
        }
        const blob = await response.blob();
        zip.file(file.name, blob);
      }

      const monthName = MONTHS[selectedMonth - 1];
      const zipName = `CPA_Binders_${monthName}_${selectedYear}.zip`;
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, zipName);
    } catch (err) {
      console.error('Failed to build zip download:', err);
      setError('Download all failed. Try downloading individual binders.');
    } finally {
      setDownloadProgress(null);
    }
  };

  const handleDownloadSingle = async (file: { name: string; url: string }) => {
    try {
      const response = await fetch(file.url);
      if (!response.ok) {
        throw new Error(`Failed to download ${file.name}`);
      }
      const blob = await response.blob();
      saveAs(blob, file.name);
    } catch (err) {
      console.error('Failed to download file:', err);
      setError(`Failed to download ${file.name}. Please try again.`);
    }
  };

  const totalSelected = selectedCategories.reduce(
    (sum, cat) => sum + expensesByCategory[cat].length, 
    0
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Export PDF Binders</h1>
        <p className="text-white/60 mt-1">Combine receipts into PDF binders for your CPA</p>
      </div>

      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Month/Year Selection */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center space-x-3 mb-4">
          <Calendar className="w-5 h-5 text-shuttle-sand" />
          <h2 className="text-lg font-semibold text-white">Select Period</h2>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-shuttle-dark border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand transition-all [&>option]:bg-shuttle-dark [&>option]:text-white"
            >
              {MONTHS.map((month, i) => (
                <option key={i} value={i + 1} className="bg-shuttle-dark text-white">{month}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-shuttle-dark border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand transition-all [&>option]:bg-shuttle-dark [&>option]:text-white"
            >
              {years.map(year => (
                <option key={year} value={year} className="bg-shuttle-dark text-white">{year}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-sm text-white/50 mt-3">
          {monthExpenses.length} document{monthExpenses.length !== 1 ? 's' : ''} in {MONTHS[selectedMonth - 1]} {selectedYear}
        </p>
      </div>

      {/* Category Selection */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Package className="w-5 h-5 text-shuttle-sand" />
            <h2 className="text-lg font-semibold text-white">Select Categories</h2>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={selectAll}
              className="text-sm text-shuttle-sky hover:text-white transition-colors"
            >
              Select All
            </button>
            <span className="text-white/30">|</span>
            <button
              onClick={clearSelection}
              className="text-sm text-shuttle-sky hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CATEGORIES.map((cat) => {
            const count = expensesByCategory[cat.value].length;
            const isSelected = selectedCategories.includes(cat.value);
            return (
              <button
                key={cat.value}
                onClick={() => toggleCategory(cat.value)}
                disabled={count === 0}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-shuttle-sand bg-shuttle-sand/20'
                    : count === 0
                    ? 'border-white/10 opacity-50 cursor-not-allowed'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <span className="text-2xl block mb-1">{cat.icon}</span>
                <p className="text-sm font-medium text-white truncate">{cat.label}</p>
                <p className="text-xs text-white/50">{count} doc{count !== 1 ? 's' : ''}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Preview */}
      {selectedCategories.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">📋 Binders to Generate</h3>
          <div className="space-y-3">
            {selectedCategories.map(cat => {
              const category = CATEGORIES.find(c => c.value === cat);
              const categoryDocs = expensesByCategory[cat];
              const count = categoryDocs.length;
              const isExpanded = expandedBinders.includes(cat);
              return (
                <div key={cat} className="bg-white/5 rounded-lg overflow-hidden">
                  {/* Binder Header - Clickable */}
                  <button
                    onClick={() => toggleBinderExpand(cat)}
                    className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">{category?.icon}</span>
                      <span className="text-white">{category?.label}_{MONTHS[selectedMonth - 1]}_{selectedYear}.pdf</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white/50 text-sm">{count} page{count !== 1 ? 's' : ''}</span>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-white/50" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white/50" />
                      )}
                    </div>
                  </button>
                  
                  {/* Document List - Expandable */}
                  {isExpanded && (
                    <div className="border-t border-white/10 bg-black/20">
                      <div className="max-h-64 overflow-y-auto">
                        {categoryDocs.map((doc, idx) => (
                          <div 
                            key={doc.id} 
                            className="flex items-center justify-between px-4 py-2 border-b border-white/5 last:border-b-0 hover:bg-white/5"
                          >
                            <div className="flex items-center space-x-3 min-w-0 flex-1">
                              <span className="text-white/40 text-xs w-5">{idx + 1}.</span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-white truncate">{doc.filename}</p>
                                <p className="text-xs text-white/40 truncate">
                                  {doc.description || 'No description'} • {new Date(doc.date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            {doc.amount && (
                              <span className="text-sm text-green-400 font-medium ml-2">
                                ${doc.amount.toFixed(2)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="px-4 py-2 bg-white/5 border-t border-white/10">
                        <p className="text-xs text-white/50">
                          Total: ${categoryDocs.reduce((sum, d) => sum + (d.amount || 0), 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-sm text-white/50 mt-3">
            Total: {totalSelected} document{totalSelected !== 1 ? 's' : ''} in {selectedCategories.length} binder{selectedCategories.length !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-white/40 mt-1">
            💡 Click a binder to see all documents inside
          </p>
        </div>
      )}

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isGenerating || selectedCategories.length === 0}
        className="w-full flex flex-col items-center justify-center py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isGenerating ? (
          <>
            <div className="flex items-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Generating PDF Binders...</span>
            </div>
            {generationProgress && (
              <div className="mt-2 text-sm opacity-80">
                Processing {generationProgress.current}/{generationProgress.total}: {generationProgress.filename}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>Generate PDF Binders</span>
          </div>
        )}
      </button>

      {/* Generated Files */}
      {generatedFiles.length > 0 && (
        <div className="bg-green-500/20 backdrop-blur-md rounded-2xl p-6 border border-green-500/50">
          <div className="flex items-center space-x-3 mb-4">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Binders Ready!</h3>
          </div>
          
          <div className="space-y-2 mb-4">
            {generatedFiles.map((file, i) => (
              <button
                key={i}
                onClick={() => handleDownloadSingle(file)}
                className="w-full flex items-center justify-between p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-white" />
                  <span className="text-white">{file.name}</span>
                </div>
                <Download className="w-5 h-5 text-shuttle-sand" />
              </button>
            ))}
          </div>

          {downloadProgress && (
            <div className="mb-3 p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center justify-between text-xs text-white/60 mb-2">
                <span>Preparing ZIP...</span>
                <span>{downloadProgress.current}/{downloadProgress.total}</span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-shuttle-sand transition-all"
                  style={{
                    width: `${Math.round((downloadProgress.current / downloadProgress.total) * 100)}%`,
                  }}
                />
              </div>
              {downloadProgress.filename && (
                <p className="text-xs text-white/50 mt-2 truncate">{downloadProgress.filename}</p>
              )}
            </div>
          )}

          {generatedFiles.length > 1 && (
            <button
              onClick={handleDownloadAll}
              disabled={!!downloadProgress}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Package className="w-5 h-5" />
              <span>Download All</span>
            </button>
          )}

          <p className="text-xs text-white/50 text-center mt-4">
            💡 Send these to your CPA by the 8th of the month!
          </p>
        </div>
      )}
    </div>
  );
}

